import { useLazyQuery } from '@apollo/client';
import { Rights } from '@dokucoach/common-types';
import Button from 'components/Button';
import BaseDataResidentsAsideMenu from 'components/Layout/components/BaseDataAside/BaseDataResidentsAsideMenu';
import CategorySelect from 'components/Layout/components/BaseDataAside/CategorySelect';
import BaseDataAsideContext from 'components/Layout/components/BaseDataAside/context';
import { useContextSnackbar } from 'components/Snackbar/context';
import { BaseDataList } from 'constants/base-data-list';
import routePaths from 'constants/route-paths';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { GET_ALL_DOCTORS } from 'services/doctors';
import { GET_EMPLOYEES } from 'services/employees';
import { GET_ALL_INSURANCES } from 'services/insurances';
import { GET_RESIDENTS } from 'services/resident';
import { GET_ALL_WELFARE_OFFICES, WelfareOfficeListData } from 'services/welfareOffices';
import { getPath } from 'utils/path';
import { useContextAuthManager } from '../../../AuthManager';
import { useContextLayout } from '../../context';
import { Footer } from '../common/styled-components';
import Search from '../Search';
import BaseDataDoctorsAsideMenu from './BaseDataDoctorsAsideMenu';
import BaseDataEmployeesAsideMenu from './BaseDataEmployeesAsideMenu';
import BaseDataInsuranceAsideMenu from './BaseDataInsuranceAsideMenu';
import BaseDataWelfareOfficeAsideMenu from './BaseDataWelfareOfficeAsideMenu';
import Filter from './Filter';

const BaseDataAside = () => {
  const { t } = useTranslation();
  const { showApiError } = useContextSnackbar();
  const history = useHistory();
  const {
    baseDataListSelected,
    employees,
    setEmployees,
    setFilteredEmployees,
    residents,
    setResidents,
    setFilteredResidents,
    doctors,
    setDoctors,
    setFilteredDoctors,
    insurances,
    setInsurances,
    setFilteredInsurances,
    welfareOffices,
    setWelfareOffices,
    setFilteredWelfareOffices,
    setBaseDataListSelected,
    resetAllFilteredLists,
    shouldRefetch,
    setShouldRefetch,
  } = useContextLayout();
  const { hasPermission, user } = useContextAuthManager();
  const [nursingHomeId, setNursingHomeId] = useState<string>('');
  const [getEmployees, { data: dataEmployees, error: errorEmployees, refetch: refetchEmployees }] =
    useLazyQuery(GET_EMPLOYEES);
  const [getResidents, { data: dataResidents, error: errorResidents, refetch: refetchResidents }] =
    useLazyQuery(GET_RESIDENTS);
  const [getDoctors, { data: dataDoctors, error: errorDoctors, refetch: refetchDoctors }] =
    useLazyQuery(GET_ALL_DOCTORS, { variables: { isDeleted: false } });
  const [
    getInsurances,
    { data: dataInsurances, error: errorInsurances, refetch: refetchInsurances },
  ] = useLazyQuery(GET_ALL_INSURANCES);
  const [
    getWelfareOffices,
    { data: dataWelfareOffices, error: errorWelfareOffices, refetch: refetchWelfareOffices },
  ] = useLazyQuery(GET_ALL_WELFARE_OFFICES);

  type MenuState = 'active' | 'inactive' | 'deleted' | 'absent' | 'applicant';
  const [selectedFilter, setSelectedFilter] = useState<MenuState | undefined>('active');

  const employeeFilterOptions = useMemo(
    () => [
      { value: 'active', text: t('employee_filter_active') },
      { value: 'inactive', text: t('employee_filter_inactive') },
    ],
    [t],
  );

  const residentFilterOptions = useMemo(
    () => [
      { value: 'active', text: t('resident_filter_active') }, // isactive === true
      { value: 'inactive', text: t('resident_filter_inactive') }, // isactive === false
      { value: 'deleted', text: t('resident_filter_deleted') }, // deleted_at !== null
      { value: 'absent', text: t('resident_filter_absent') }, // isAbsent = true
      { value: 'applicant', text: t('resident_filter_applicant') }, // isApplicant = true
    ],
    [t],
  );

  const doctorFilterOptions = useMemo(
    () => [
      { value: 'active', text: t('doctor_filter_active') },
      { value: 'inactive', text: t('doctor_filter_inactive') },
    ],
    [t],
  );

  /*
 Commenting out this feature because currently welfare offices can only be considered as active
 const welfareOfficeFilterOptions = useMemo(
  () => [
    { value: 'active', text: t('welfareOffice_filter_active') },
    { value: 'inactive', text: t('welfareOffice_filter_inactive') },
  ],
  [t],
);
*/

  useEffect(() => {
    setNursingHomeId(user?.nursingHome.id ?? '');
  }, [user]);

  useEffect(() => {
    if (!history.location.pathname) return;

    if (history.location.pathname.includes(routePaths.DELETED)) {
      setSelectedFilter('deleted');
    } else if (history.location.pathname.includes(routePaths.INACTIVE)) {
      setSelectedFilter('inactive');
    } else if (history.location.pathname.includes(routePaths.ABSENT)) {
      setSelectedFilter('absent');
    } else if (history.location.pathname.includes(routePaths.APPLICANT)) {
      setSelectedFilter('applicant');
    } else {
      setSelectedFilter('active');
    }
  }, [history.location.pathname]);

  useEffect(() => {
    if (selectedFilter === undefined) return;

    switch (baseDataListSelected) {
      case BaseDataList.EMPLOYEES:
        getEmployees({ variables: { isDeleted: selectedFilter === 'inactive' } });
        break;
      case BaseDataList.RESIDENTS:
        getResidents({
          variables: {
            ...(selectedFilter === 'applicant'
              ? { isApplicant: true }
              : {
                  isDeleted: selectedFilter === 'deleted',
                  isActive: selectedFilter === 'absent' || selectedFilter === 'active',
                  isAbsent: selectedFilter === 'absent',
                  isApplicant: false,
                }),
          },
        });
        break;
      case BaseDataList.DOCTORS:
        getDoctors({ variables: { isDeleted: selectedFilter === 'inactive' } });
        break;
      case BaseDataList.INSURANCE:
        getInsurances({ variables: { isDeleted: selectedFilter === 'inactive' } });
        break;
      case BaseDataList.WELFARE_OFFICES:
        getWelfareOffices({ variables: { isDeleted: selectedFilter === 'inactive' } });
        break;
      default:
        break;
    }
  }, [
    selectedFilter,
    baseDataListSelected,
    getEmployees,
    getResidents,
    getDoctors,
    getInsurances,
    getWelfareOffices,
  ]);

  useEffect(() => {
    if (errorEmployees) {
      showApiError(errorEmployees);
      return;
    }
    if (!dataEmployees) return;
    setEmployees(dataEmployees.getEmployees);
    setFilteredEmployees([...dataEmployees.getEmployees]);
  }, [dataEmployees, errorEmployees, setEmployees, setFilteredEmployees, showApiError]);

  useEffect(() => {
    if (errorResidents) {
      showApiError(errorResidents);
      return;
    }
    if (!dataResidents) return;
    setResidents(dataResidents.getResidents);
    setFilteredResidents([...dataResidents.getResidents]);
  }, [dataResidents, errorResidents, setResidents, setFilteredResidents, showApiError]);

  useEffect(() => {
    if (errorDoctors) {
      showApiError(errorDoctors);
      return;
    }
    if (!dataDoctors) return;
    setDoctors(dataDoctors.getAllDoctors);
    setFilteredDoctors([...dataDoctors.getAllDoctors]);
  }, [dataDoctors, errorDoctors, setDoctors, setFilteredDoctors, showApiError]);

  useEffect(() => {
    if (errorInsurances) {
      showApiError(errorInsurances);
      return;
    }
    if (!dataInsurances) return;
    setInsurances(dataInsurances.getInsurances);
    setFilteredInsurances([...dataInsurances.getInsurances]);
  }, [dataInsurances, errorInsurances, setInsurances, setFilteredInsurances, showApiError]);

  useEffect(() => {
    if (errorWelfareOffices) {
      showApiError(errorWelfareOffices);
      return;
    }
    if (!dataWelfareOffices) return;
    const filtered = dataWelfareOffices.getWelfareOffices.filter(
      (welfareOffice: WelfareOfficeListData) =>
        welfareOffice.id.includes('WOId' + nursingHomeId) === false,
    );
    setWelfareOffices(filtered);
    setFilteredWelfareOffices([...filtered]);
  }, [
    dataWelfareOffices,
    errorWelfareOffices,
    setWelfareOffices,
    setFilteredWelfareOffices,
    showApiError,
    nursingHomeId,
  ]);

  useEffect(() => {
    if (shouldRefetch && baseDataListSelected === BaseDataList.EMPLOYEES && refetchEmployees) {
      refetchEmployees().then(() => setShouldRefetch(undefined));
    } else if (
      shouldRefetch &&
      baseDataListSelected === BaseDataList.RESIDENTS &&
      refetchResidents
    ) {
      refetchResidents().then(() => setShouldRefetch(undefined));
    } else if (shouldRefetch && baseDataListSelected === BaseDataList.DOCTORS && refetchDoctors) {
      refetchDoctors().then(() => setShouldRefetch(undefined));
    } else if (
      shouldRefetch &&
      baseDataListSelected === BaseDataList.INSURANCE &&
      refetchInsurances
    ) {
      refetchInsurances().then(() => setShouldRefetch(undefined));
    } else if (
      shouldRefetch &&
      baseDataListSelected === BaseDataList.WELFARE_OFFICES &&
      refetchWelfareOffices
    ) {
      refetchWelfareOffices().then(() => setShouldRefetch(undefined));
    }
  }, [
    baseDataListSelected,
    refetchEmployees,
    refetchResidents,
    refetchDoctors,
    refetchInsurances,
    refetchWelfareOffices,
    setShouldRefetch,
    shouldRefetch,
  ]);

  const onSearch = (term: string) => {
    if (!term || term === '') {
      resetAllFilteredLists();
      return;
    }
    if (baseDataListSelected === BaseDataList.EMPLOYEES) {
      const filtered = employees.filter((employee) =>
        (employee.name.firstFull + ' ' + employee.name.last)
          .toLowerCase()
          .includes(term.toLowerCase()),
      );
      setFilteredEmployees(filtered);
    } else if (baseDataListSelected === BaseDataList.RESIDENTS) {
      const filtered = residents.filter((resident) =>
        (resident.name.firstFull + ' ' + resident.name.last)
          .toLowerCase()
          .includes(term.toLowerCase()),
      );
      setFilteredResidents(filtered);
    } else if (baseDataListSelected === BaseDataList.DOCTORS) {
      const filtered = doctors.filter((doctor) =>
        (doctor.name.firstFull + ' ' + doctor.name.last).toLowerCase().includes(term.toLowerCase()),
      );
      setFilteredDoctors(filtered);
    } else if (baseDataListSelected === BaseDataList.INSURANCE) {
      const filtered = insurances.filter((insurance) =>
        insurance.name.toLowerCase().includes(term.toLowerCase()),
      );
      setFilteredInsurances(filtered);
    } else if (baseDataListSelected === BaseDataList.WELFARE_OFFICES) {
      const filtered = welfareOffices.filter((welfareOffice) =>
        welfareOffice.name.toLowerCase().includes(term.toLowerCase()),
      );
      setFilteredWelfareOffices(filtered);
    }
  };

  const onSelectDataList = (e: string | null): void => {
    if (!e) return;
    resetAllFilteredLists();
    if (history.location.pathname !== getPath([routePaths.BASE_DATA])) {
      history.push(getPath([routePaths.BASE_DATA]));
    }
    const selected = e as BaseDataList;
    switch (selected) {
      case BaseDataList.EMPLOYEES:
        getEmployees({ variables: { isDeleted: false } });
        setBaseDataListSelected(BaseDataList.EMPLOYEES);
        break;
      case BaseDataList.RESIDENTS:
        getResidents();
        setBaseDataListSelected(BaseDataList.RESIDENTS);
        break;
      case BaseDataList.DOCTORS:
        getDoctors({ variables: { isDeleted: false } });
        setBaseDataListSelected(BaseDataList.DOCTORS);
        break;
      case BaseDataList.INSURANCE:
        getInsurances({ variables: { isDeleted: false } });
        setBaseDataListSelected(BaseDataList.INSURANCE);
        break;
      case BaseDataList.WELFARE_OFFICES:
        getWelfareOffices({ variables: { isDeleted: false } });
        setBaseDataListSelected(BaseDataList.WELFARE_OFFICES);
        break;
      default:
        break;
    }
  };

  const onAddNew = () => {
    switch (baseDataListSelected) {
      case BaseDataList.RESIDENTS:
        history.push(getPath([routePaths.DATA_ADD_RESIDENT]));
        break;

      case BaseDataList.EMPLOYEES:
        history.push(getPath([routePaths.DATA_ADD_EMPLOYEE]));
        break;

      case BaseDataList.DOCTORS:
        history.push(getPath([routePaths.DATA_ADD_DOCTOR]));
        break;
      case BaseDataList.INSURANCE:
        history.push(getPath([routePaths.DATA_ADD_INSURANCE]));
        break;
      case BaseDataList.WELFARE_OFFICES:
        history.push(getPath([routePaths.DATA_ADD_WELFARE_OFFICE]));
        break;
    }
  };

  const onFilter = (value: string | null) => {
    if (!value) return;

    const currentPath: { [key: string]: string } = {
      deleted: routePaths.DELETED,
      absent: routePaths.ABSENT,
      inactive: routePaths.INACTIVE,
      applicant: routePaths.APPLICANT,
    };

    history.push(`${getPath([routePaths.BASE_DATA])}${currentPath[value] ?? ''}`);
    setSelectedFilter(value as MenuState);
  };

  return (
    <BaseDataAsideContext>
      <div className="aside-secondary d-flex flex-row-fluid" style={{ height: '100vh' }}>
        <div className="aside-workspace scroll scroll-push my-2">
          <div className="tab-content d-flex flex-column h-100">
            <div className={`tab-pane p-3 px-lg-6 py-lg-2 fade show active`}>
              <div className={'mb-4'}>
                <CategorySelect onSelect={onSelectDataList} />
              </div>
              <div className="d-flex">
                <Search onSearch={onSearch} />
                {baseDataListSelected === BaseDataList.EMPLOYEES && (
                  <Filter
                    options={employeeFilterOptions}
                    onSelect={onFilter}
                    initialActive={
                      history.location.pathname.includes(routePaths.INACTIVE)
                        ? 'inactive'
                        : undefined
                    }
                  />
                )}
                {baseDataListSelected === BaseDataList.RESIDENTS && (
                  <Filter
                    options={residentFilterOptions}
                    onSelect={onFilter}
                    initialActive={
                      history.location.pathname.includes(routePaths.INACTIVE)
                        ? 'inactive'
                        : history.location.pathname.includes(routePaths.DELETED)
                        ? 'deleted'
                        : history.location.pathname.includes(routePaths.ABSENT)
                        ? 'absent'
                        : history.location.pathname.includes(routePaths.APPLICANT)
                        ? 'applicant'
                        : undefined
                    }
                  />
                )}
                {baseDataListSelected === BaseDataList.DOCTORS && (
                  <Filter
                    options={doctorFilterOptions}
                    onSelect={onFilter}
                    initialActive={
                      history.location.pathname.includes(routePaths.INACTIVE)
                        ? 'inactive'
                        : undefined
                    }
                  />
                )}
                {/*
               Commenting out this feature because currently welfare offices can only be considered as active
                {baseDataListSelected === BaseDataList.WELFARE_OFFICES && (
                  <Filter
                    options={welfareOfficeFilterOptions}
                    onSelect={onFilter}
                    initialActive={
                      history.location.pathname.includes(routePaths.INACTIVE)
                        ? 'inactive'
                        : undefined
                    }
                  />
                )}
                */}
              </div>
            </div>
            {baseDataListSelected === BaseDataList.EMPLOYEES && (
              <>
                <BaseDataEmployeesAsideMenu />
                {hasPermission(Rights.EMPLOYEES.WRITE) && (
                  <Footer className="px-lg-6 p-3">
                    <Button $lowerCase={true} className="w-100" onClick={onAddNew}>
                      {t('baseData_add_employees')}
                    </Button>
                  </Footer>
                )}
              </>
            )}
            {baseDataListSelected === BaseDataList.RESIDENTS && (
              <>
                <BaseDataResidentsAsideMenu />
                {selectedFilter === 'active' && hasPermission(Rights.RESIDENTS.WRITE) && (
                  <Footer className="px-lg-6 p-3">
                    <Button $lowerCase={true} className="w-100" onClick={onAddNew}>
                      {t('baseData_add_residents')}
                    </Button>
                  </Footer>
                )}
              </>
            )}
            {baseDataListSelected === BaseDataList.DOCTORS && (
              <>
                <BaseDataDoctorsAsideMenu />
                {hasPermission(Rights.BASE_DATA.DOCTORS.WRITE) && (
                  <Footer className="px-lg-6 p-3">
                    <Button $lowerCase={true} className="w-100" onClick={onAddNew}>
                      {t('baseData_add_doctors')}
                    </Button>
                  </Footer>
                )}
              </>
            )}
            {baseDataListSelected === BaseDataList.INSURANCE && (
              <>
                <BaseDataInsuranceAsideMenu />
                {hasPermission(Rights.BASE_DATA.DOCTORS.WRITE) && (
                  <Footer className="px-lg-6 p-3">
                    <Button $lowerCase={true} className="w-100" onClick={onAddNew}>
                      {t('baseData_add_insurances')}
                    </Button>
                  </Footer>
                )}
              </>
            )}
            {baseDataListSelected === BaseDataList.WELFARE_OFFICES && (
              <>
                <BaseDataWelfareOfficeAsideMenu />
                {hasPermission(Rights.BASE_DATA.WELFARE_OFFICE.WRITE) && (
                  <Footer className="px-lg-6 p-3">
                    <Button $lowerCase={true} className="w-100" onClick={onAddNew}>
                      {t('baseData_add_welfareOffices')}
                    </Button>
                  </Footer>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </BaseDataAsideContext>
  );
};

export default BaseDataAside;
